import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { User } from '../types';
import i18n from '../i18n/index';
import { syncPublicUserData } from './syncService';

/**
 * Sanitize object by removing undefined values (Firestore rejects undefined)
 */
const sanitizeForFirestore = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const sanitized: Partial<T> = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      sanitized[key] = obj[key];
    }
  }
  return sanitized;
};

/**
 * CRITICAL: Ensure user profile exists in Firestore after successful authentication
 * This is the SINGLE SOURCE OF TRUTH for profile creation after auth
 * 
 * @param uid - Firebase Auth UID (must match request.auth.uid for Firestore rules)
 * @param userData - User data from auth (email, displayName, photoURL, etc.)
 * @param extraFields - Optional additional fields (username, referralCode, etc.)
 * @returns Created or updated User object
 */
export const ensureUserProfile = async (
  uid: string,
  userData: {
    email: string;
    displayName?: string | null;
    photoURL?: string | null;
    provider?: 'password' | 'google' | 'facebook';
  },
  extraFields?: {
    username?: string;
    referralCode?: string;
    referredBy?: string;
    isAdmin?: boolean;
  }
): Promise<User> => {
  // CRITICAL: Validate UID
  if (!uid || typeof uid !== 'string') {
    console.error('[PROFILE] ❌ Invalid UID:', uid);
    throw new Error('Invalid user ID: UID must be a non-empty string');
  }

  // Only log in development - production console should be clean
  if (import.meta.env.DEV) {
    console.log('[PROFILE] 🔍 Ensuring profile exists for UID:', uid);
  }

  const userRef = doc(db, "users", uid);
  
  // Check if profile already exists
  let userSnap;
  try {
    userSnap = await getDoc(userRef);
  } catch (error: any) {
    console.error('[PROFILE] ❌ Error checking profile existence:', {
      code: error.code,
      message: error.message,
      uid
    });
    throw new Error(`Failed to check profile: ${error.message || 'Unknown error'}`);
  }

  const displayName = userData.displayName || userData.email.split('@')[0] || i18n.t('errors.new_user');
  const avatar = userData.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${uid}`;
  
  if (userSnap.exists()) {
    // Profile exists - update lastLoginAt and ensure required fields exist
    // Only log in development
    if (import.meta.env.DEV) {
      console.log('[PROFILE] ✅ Profile exists, updating lastLoginAt and ensuring required fields');
    }
    const existingData = userSnap.data() as User;
    
    const updates: Partial<User> = {
      lastLoginAt: serverTimestamp()
    };
    
    // ADMIN SETUP: Update role if user is admin email
    const ADMIN_EMAILS = ['cristianp.business8@gmail.com', 'pellegrinoluca123@gmail.com'];
    const isAdminEmail = ADMIN_EMAILS.some(adminEmail => userData.email.toLowerCase() === adminEmail.toLowerCase());
    if (isAdminEmail && existingData.role !== 'admin') {
      updates.role = 'admin';
      // Admin role updates should be logged (security-relevant)
      if (import.meta.env.DEV) {
        console.log('[PROFILE] 🔐 Updating user role to admin for:', userData.email);
      }
    }
    
    // CRITICAL: Ensure all required fields exist (fix incomplete profiles)
    if (!existingData.name && userData.displayName) {
      updates.name = userData.displayName;
    }
    if (!existingData.avatar && userData.photoURL) {
      updates.avatar = userData.photoURL;
    }
    if (!existingData.provider && userData.provider) {
      updates.provider = userData.provider;
    }
    // Ensure xp and level exist (required for leaderboard/search)
    if (existingData.xp === undefined || existingData.xp === null) {
      updates.xp = 0;
    }
    if (existingData.level === undefined || existingData.level === null) {
      updates.level = 1;
    }
    // Ensure arrays exist
    if (!Array.isArray(existingData.following)) {
      updates.following = [];
    }
    if (!Array.isArray(existingData.badges)) {
      updates.badges = [];
    }
    if (!Array.isArray(existingData.activeChallenges)) {
      updates.activeChallenges = [];
    }
    
    try {
      const sanitizedUpdates = sanitizeForFirestore(updates);
      if (Object.keys(sanitizedUpdates).length > 1) { // More than just lastLoginAt
        await updateDoc(userRef, sanitizedUpdates);
        // Only log in development
        if (import.meta.env.DEV) {
          console.log('[PROFILE] ✅ Profile updated with required fields');
        }
      } else {
        // Only lastLoginAt needs updating
        await updateDoc(userRef, sanitizedUpdates);
        // Only log in development
        if (import.meta.env.DEV) {
          console.log('[PROFILE] ✅ Profile lastLoginAt updated');
        }
      }
      const updatedUser = { ...existingData, ...sanitizedUpdates, id: uid } as User;
      
      // CRITICAL: Sync public data after profile update/creation
      // Check if any public fields were updated
      const publicFields = ['name', 'avatar', 'photoURL', 'level', 'xp', 'title'];
      const hasPublicFieldUpdate = publicFields.some(field => field in sanitizedUpdates);
      
      if (hasPublicFieldUpdate || !userSnap.exists()) {
        // Sync in background (don't await - non-blocking)
        syncPublicUserData(uid, updatedUser).catch(() => {
          // Errors already logged in syncService
        });
      }
      
      return updatedUser;
    } catch (error: any) {
      console.error('[PROFILE] ❌ Error updating existing profile:', {
        code: error.code,
        message: error.message,
        uid,
        updates: Object.keys(updates)
      });
      // Don't throw - return existing data even if update fails
      return existingData;
    }
  }

  // Profile doesn't exist - create it with ALL required fields
  // Only log in development
  if (import.meta.env.DEV) {
    console.log('[PROFILE] 📝 Creating new profile for UID:', uid);
  }
  
  // CRITICAL: Build complete profile object with ALL required fields
  // This ensures the user is discoverable in search, feed, and leaderboard
  const newProfile: Partial<User> = {
    id: uid,
    email: userData.email,
    name: displayName,
    avatar: avatar,
    role: extraFields?.isAdmin ? 'admin' : 'user',
    plan: 'free',
    planStatus: 'active',
    level: 1,
    xp: 0, // Required for leaderboard
    streak: 0,
    hasAcceptedTerms: false,
    hasOnboarded: false,
    following: [], // Required array field
    badges: [], // Required array field
    activeChallenges: [], // Required array field
    createdAt: serverTimestamp(),
    lastLoginAt: serverTimestamp()
  };

  // Add optional fields only if provided
  if (userData.provider) {
    newProfile.provider = userData.provider;
  }
  if (extraFields?.username) {
    newProfile.username = extraFields.username.trim().toLowerCase();
  }
  if (extraFields?.referralCode) {
    newProfile.referralCode = extraFields.referralCode;
  }
  if (extraFields?.referredBy) {
    newProfile.referredBy = extraFields.referredBy;
  }
  
  // Initialize referral stats if needed
  if (extraFields?.referredBy || extraFields?.referralCode) {
    newProfile.referralStats = {
      totalInvites: 0,
      confirmedSubscriptions: 0,
      currentDiscountPercent: 0,
      totalXPEarned: 0,
      discountsUsed: 0
    };
    newProfile.referralRewards = {
      earned: 0,
      pending: 0
    };
  }

  // CRITICAL: Sanitize to remove any undefined values
  const sanitizedProfile = sanitizeForFirestore(newProfile);
  
  // Only log in development
  if (import.meta.env.DEV) {
    console.log('[PROFILE] 📤 Writing profile to Firestore:', {
      uid,
      email: userData.email,
      name: displayName,
      fields: Object.keys(sanitizedProfile)
    });
  }

  try {
    // CRITICAL: Use setDoc WITHOUT merge for new documents to ensure ALL fields are written
    // This guarantees the profile is complete and discoverable
    // setDoc (without merge) will create the document with all specified fields
    await setDoc(userRef, sanitizedProfile);
    
    // CRITICAL: Sync public data after profile creation
    const newUser = { ...sanitizedProfile, id: uid } as User;
    // Sync in background (don't await - non-blocking)
    syncPublicUserData(uid, newUser).catch(() => {
      // Errors already logged in syncService
    });
    
    // Only log in development
    if (import.meta.env.DEV) {
      console.log('[PROFILE] ✅ Profile created successfully with all required fields:', {
        uid,
        email: userData.email,
        name: displayName,
        xp: 0,
        level: 1,
        fields: Object.keys(sanitizedProfile),
        environment: typeof window !== 'undefined' ? window.location.hostname : 'server',
        timestamp: new Date().toISOString()
      });
    }
    
    // Verify the document was created by reading it back
    const verifySnap = await getDoc(userRef);
    if (!verifySnap.exists()) {
      throw new Error('Profile creation verification failed: document does not exist after write');
    }
    
    // Only log in development
    if (import.meta.env.DEV) {
      console.log('[PROFILE] ✅ Profile creation verified - document exists in Firestore');
    }
    
    return { ...sanitizedProfile, id: uid } as User;
  } catch (error: any) {
    // CRITICAL: Explicit logging to distinguish Firestore errors from Auth errors
    console.error('[PROFILE] ❌ CRITICAL: FIRESTORE PROFILE CREATION FAILED:', {
      errorType: 'FIRESTORE_ERROR',
      code: error.code,
      message: error.message,
      uid,
      email: userData.email,
      environment: typeof window !== 'undefined' ? window.location.hostname : 'server',
      timestamp: new Date().toISOString(),
      errorObject: error,
      // Diagnostic info
      authState: 'User is authenticated (auth succeeded before this point)',
      firestoreWrite: 'Failed',
      possibleCauses: [
        'Firestore rules deny write',
        'UID mismatch (uid !== request.auth.uid)',
        'Invalid data format',
        'Network error'
      ]
    });
    
    // Provide detailed error for debugging
    if (error.code === 'permission-denied') {
      throw new Error(`Permission denied: Ensure UID (${uid}) matches request.auth.uid. Check Firestore rules.`);
    } else if (error.code === 'invalid-argument') {
      throw new Error(`Invalid data: Check for undefined values or invalid field types. Error: ${error.message}`);
    } else {
      throw new Error(`Failed to create profile: ${error.message || 'Unknown error'} (code: ${error.code || 'unknown'})`);
    }
  }
};

/**
 * Get or create user profile (legacy function - kept for backward compatibility)
 * If profile doesn't exist, creates it automatically
 */
export const getOrCreateProfile = async (userId: string, userData?: Partial<User>): Promise<User> => {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return { id: userSnap.id, ...userSnap.data() } as User;
  }

  // Profile doesn't exist - create it
  if (userData) {
    const newProfile: User = {
      id: userId,
      email: userData.email || '',
      name: userData.name || i18n.t('common.user_default'),
      avatar: userData.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`,
      level: userData.level || 1,
      xp: userData.xp || 0,
      streak: userData.streak || 0,
      role: userData.role || 'user',
      plan: userData.plan || 'free',
      planStatus: userData.planStatus || 'active',
      badges: userData.badges || [],
      activeChallenges: userData.activeChallenges || [],
      bio: userData.bio || '',
      ...userData
    };

    // Sanitize before writing
    const sanitized = sanitizeForFirestore(newProfile);
    await setDoc(userRef, sanitized, { merge: true });
    const createdUser = { ...sanitized, id: userId } as User;
    
    // CRITICAL: Sync public data after profile creation
    // Sync in background (don't await - non-blocking)
    syncPublicUserData(userId, createdUser).catch(() => {
      // Errors already logged in syncService
    });
    
    return createdUser;
  }

  throw new Error("User data is required to create a new profile");
};

/**
 * Update user profile (bio, avatar, name, username, etc.)
 * 
 * CRITICAL: This function requires:
 * - User must be authenticated
 * - userId must match request.auth.uid (enforced by Firestore rules)
 * - Profile document must exist (or will be created by Firestore rules if allowed)
 */
export const updateProfile = async (
  userId: string,
  updates: {
    bio?: string;
    avatar?: string;
    photoURL?: string;
    name?: string;
    username?: string;
    [key: string]: any;
  }
): Promise<void> => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  const userRef = doc(db, "users", userId);
  
  // Normalize username to lowercase if provided
  if (updates.username !== undefined) {
    updates.username = updates.username.trim().toLowerCase() || undefined;
  }
  
  // CRITICAL: If avatar is provided, also set photoURL (and vice versa) for consistency
  if (updates.avatar && !updates.photoURL) {
    updates.photoURL = updates.avatar;
  }
  if (updates.photoURL && !updates.avatar) {
    updates.avatar = updates.photoURL;
  }

  try {
    // Only log in development
    if (import.meta.env.DEV) {
      console.log('[PROFILE] Updating profile for userId:', userId, 'updates:', Object.keys(updates));
    }
    
    // CRITICAL: Sanitize updates to remove undefined values (Firestore rejects undefined)
    const sanitizedUpdates = sanitizeForFirestore(updates);
    
    // Only log in development
    if (import.meta.env.DEV) {
      console.log('[PROFILE] Sanitized updates:', Object.keys(sanitizedUpdates));
    }
    
    await updateDoc(userRef, sanitizedUpdates);
    // Only log in development
    if (import.meta.env.DEV) {
      console.log('[PROFILE] ✅ Profile updated successfully in Firestore');
    }
    
    // CRITICAL: Sync public data after profile update
    // Check if any public fields were updated
    const publicFields = ['name', 'avatar', 'photoURL', 'title'];
    const hasPublicFieldUpdate = publicFields.some(field => field in sanitizedUpdates);
    
    if (hasPublicFieldUpdate) {
      // Read updated user data and sync
      try {
        const updatedUserSnap = await getDoc(userRef);
        if (updatedUserSnap.exists()) {
          const updatedUser = updatedUserSnap.data() as User;
          // Sync in background (don't await - non-blocking)
          syncPublicUserData(userId, updatedUser).catch(() => {
            // Errors already logged in syncService
          });
        }
      } catch (syncError: any) {
        // Log but don't throw - sync failure shouldn't block profile update
        if (import.meta.env.DEV) {
          console.warn('[PROFILE] Error syncing public data after update:', syncError);
        }
      }
    }
  } catch (error: any) {
    console.error('[PROFILE] ❌ Error updating profile:', error);
    console.error('[PROFILE] Error code:', error.code);
    console.error('[PROFILE] Error message:', error.message);
    
    // Provide user-friendly error messages
    if (error.code === 'permission-denied') {
      throw new Error(i18n.t('errors.profile_unauthorized'));
    } else if (error.code === 'not-found') {
      throw new Error(i18n.t('errors.profile_not_found'));
    }
    
    throw error;
  }
};

/**
 * Check if username is available
 * 
 * CRITICAL: Queries /publicProfiles instead of /users (which is private)
 * Username must be synced to publicProfiles for this to work
 */
export const checkUsernameAvailability = async (username: string, excludeUserId?: string): Promise<boolean> => {
  const cleanUsername = username.trim().toLowerCase();
  if (cleanUsername.length < 3) return false;
  
  // CRITICAL: Query publicProfiles instead of users (private collection)
  // Username must be synced to publicProfiles for this to work
  const publicProfilesRef = collection(db, "publicProfiles");
  const q = query(publicProfilesRef, where("username", "==", cleanUsername));
  const snap = await getDocs(q);
  
  // If excludeUserId is provided, allow if it's the same user (user is keeping their current username)
  if (excludeUserId) {
    return snap.empty || snap.docs.every(doc => doc.id === excludeUserId || doc.data().userId === excludeUserId);
  }
  
  return snap.empty;
};

/**
 * Check if user can change username (30-day limit)
 */
export const canChangeUsername = (user: User): { canChange: boolean; daysRemaining?: number } => {
  if (!user.usernameLastChanged) {
    return { canChange: true };
  }
  
  const lastChanged = user.usernameLastChanged.toDate ? user.usernameLastChanged.toDate() : new Date(user.usernameLastChanged);
  const now = new Date();
  const daysSinceChange = Math.floor((now.getTime() - lastChanged.getTime()) / (1000 * 60 * 60 * 24));
  const daysRemaining = 30 - daysSinceChange;
  
  return {
    canChange: daysRemaining <= 0,
    daysRemaining: daysRemaining > 0 ? daysRemaining : undefined
  };
};

/**
 * Get user profile by ID
 * 
 * ⚠️ DEPRECATED: This function reads from /users which is PRIVATE.
 * 
 * DO NOT USE for other users' profiles. Use getPublicProfile from publicProfileService instead.
 * 
 * This function should ONLY be used for the current user's own profile.
 * For other users, use: import { getPublicProfile } from './publicProfileService';
 * 
 * @deprecated Use getPublicProfile for other users, or getOrCreateProfile for own profile
 */
export const getProfile = async (userId: string): Promise<User | null> => {
  // CRITICAL: This function reads from /users which is PRIVATE
  // Only use this for the current user's own profile
  // For other users, use getPublicProfile from publicProfileService
  if (import.meta.env.DEV) {
    console.warn('[PROFILE] ⚠️ getProfile() reads from /users (PRIVATE). Use getPublicProfile() for other users.');
  }
  
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    return null;
  }

  return { id: userSnap.id, ...userSnap.data() } as User;
};

